/**
 * Export utilities for CSV/Excel export functionality
 */

/**
 * Convert an array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} columns - Array of column definitions { key, header }
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (data, columns) => {
  if (!data || data.length === 0) return '';
  
  // Create header row
  const headers = columns.map(col => `"${col.header}"`).join(',');
  
  // Create data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.key];
      
      // Handle nested values with dot notation
      if (col.key.includes('.')) {
        const keys = col.key.split('.');
        value = keys.reduce((obj, key) => obj?.[key], item);
      }
      
      // Handle null/undefined
      if (value === null || value === undefined) value = '';
      
      // Handle dates
      if (col.type === 'date' && value) {
        value = new Date(value).toLocaleDateString();
      }
      
      // Handle datetime
      if (col.type === 'datetime' && value) {
        value = new Date(value).toLocaleString();
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        value = value.join('; ');
      }
      
      // Handle objects
      if (typeof value === 'object' && value !== null) {
        value = JSON.stringify(value);
      }
      
      // Escape quotes and wrap in quotes
      return `"${String(value).replace(/"/g, '""')}"`;
    }).join(',');
  });
  
  return [headers, ...rows].join('\n');
};

/**
 * Download data as CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Name of the file (without extension)
 */
export const downloadCSV = (data, columns, filename) => {
  const csv = convertToCSV(data, columns);
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Download data as Excel file (XLSX format using CSV with Excel-specific headers)
 * @param {Array} data - Array of objects to export
 * @param {Array} columns - Column definitions
 * @param {string} filename - Name of the file (without extension)
 */
export const downloadExcel = (data, columns, filename) => {
  // For true Excel format, we'd need a library like xlsx
  // For now, we'll use CSV with .xlsx extension which Excel can open
  const csv = convertToCSV(data, columns);
  const blob = new Blob(['\ufeff' + csv], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Column definitions for different modules
export const EXPORT_COLUMNS = {
  patients: [
    { key: 'patient_number', header: 'Patient ID' },
    { key: 'full_name', header: 'Full Name' },
    { key: 'gender', header: 'Gender' },
    { key: 'date_of_birth', header: 'Date of Birth', type: 'date' },
    { key: 'phone', header: 'Phone' },
    { key: 'email', header: 'Email' },
    { key: 'blood_group', header: 'Blood Group' },
    { key: 'address', header: 'Address' },
    { key: 'city', header: 'City' },
    { key: 'state', header: 'State' },
    { key: 'zip_code', header: 'ZIP Code' },
    { key: 'emergency_contact_name', header: 'Emergency Contact' },
    { key: 'emergency_contact_phone', header: 'Emergency Phone' },
    { key: 'insurance_provider', header: 'Insurance Provider' },
    { key: 'insurance_policy_number', header: 'Policy Number' },
    { key: 'created_at', header: 'Registered On', type: 'datetime' }
  ],
  
  visits: [
    { key: 'visit_number', header: 'Visit ID' },
    { key: 'patient.full_name', header: 'Patient Name' },
    { key: 'patient.patient_number', header: 'Patient ID' },
    { key: 'visit_type', header: 'Visit Type' },
    { key: 'status', header: 'Status' },
    { key: 'priority', header: 'Priority' },
    { key: 'department', header: 'Department' },
    { key: 'chief_complaint', header: 'Chief Complaint' },
    { key: 'assigned_doctor_name', header: 'Assigned Doctor' },
    { key: 'created_at', header: 'Check-in Time', type: 'datetime' }
  ],
  
  admissions: [
    { key: 'room_number', header: 'Room Number' },
    { key: 'patient_name', header: 'Patient Name' },
    { key: 'patient_number', header: 'Patient ID' },
    { key: 'doctor_name', header: 'Attending Doctor' },
    { key: 'admission_reason', header: 'Admission Reason' },
    { key: 'status', header: 'Status' },
    { key: 'admission_date', header: 'Admission Date', type: 'datetime' },
    { key: 'discharge_date', header: 'Discharge Date', type: 'datetime' },
    { key: 'notes', header: 'Notes' }
  ],
  
  dischargeSummaries: [
    { key: 'patient.full_name', header: 'Patient Name' },
    { key: 'patient.patient_number', header: 'Patient ID' },
    { key: 'doctor_name', header: 'Attending Doctor' },
    { key: 'diagnosis_at_discharge', header: 'Diagnosis' },
    { key: 'condition_at_discharge', header: 'Condition' },
    { key: 'treatment_given', header: 'Treatment Given' },
    { key: 'medications_prescribed', header: 'Medications' },
    { key: 'follow_up_instructions', header: 'Follow-up Instructions' },
    { key: 'next_visit_date', header: 'Next Visit', type: 'date' },
    { key: 'created_at', header: 'Discharge Date', type: 'datetime' }
  ],
  
  medicalRecords: [
    { key: 'patient_name', header: 'Patient Name' },
    { key: 'patient_number', header: 'Patient ID' },
    { key: 'doctor_name', header: 'Doctor' },
    { key: 'diagnosis', header: 'Diagnosis' },
    { key: 'symptoms', header: 'Symptoms' },
    { key: 'prescription', header: 'Prescription' },
    { key: 'status', header: 'Status' },
    { key: 'notes', header: 'Notes' },
    { key: 'created_at', header: 'Created On', type: 'datetime' }
  ]
};
